import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Spinner } from "../components/ui";
import { TrendingUp, BarChart2, Home, AlertTriangle, Wrench, Clock, Package, MapPinned } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function Analysis() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [benchmarkCountry, setBenchmarkCountry] = useState("ALL");

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get("/analytics");
        setData(res.available ? res.data : null);
        if (!res.available) setErr(t("analysis.noSnapshot"));
      } catch (e) {
        setErr(t("analysis.loadFailed"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="p-10"><Spinner /></div>;
  if (err || !data) return (
    <div className="p-10 font-mono text-sm text-signal-yellow">
      {err || t("analysis.noData")}
    </div>
  );

  const generated = data.generated ? new Date(data.generated).toLocaleString("de-AT") : "—";
  const benchmark = data.district_offer_benchmark || {};
  const benchmarkRows = benchmark.rows || [];
  const benchmarkCountries = [...new Set(benchmarkRows.map((r) => r.country || benchmark.source?.country).filter(Boolean))].sort();
  const filteredBenchmarkRows = benchmarkRows.filter((r) => {
    const rowCountry = r.country || benchmark.source?.country || "";
    return benchmarkCountry === "ALL" || rowCountry === benchmarkCountry;
  });
  const cityBenchmarkRows = topBenchmarkRows(filteredBenchmarkRows, "city_total");
  const districtBenchmarkRows = topBenchmarkRows(filteredBenchmarkRows, "district_or_plz");

  return (
    <div className="reveal">
      <div className="border-b border-border px-6 md:px-10 py-6">
        <div className="overline text-text-muted">{t("analysis.eyebrow")}</div>
        <h1 className="mt-1 font-heading text-2xl md:text-4xl font-black tracking-tight">{t("analysis.title")}</h1>
        <p className="mt-1 text-xs font-mono text-text-muted">
          {t("analysis.subtitle", { count: data.listing_count?.toLocaleString(), from: data.date_range?.from, to: data.date_range?.to, generated })}
        </p>
      </div>

      <div className="divide-y divide-border">
        {/* ── 1. MARKET RANKINGS ── */}
        <Section icon={TrendingUp} title={t("analysis.s1Title")} sub={t("analysis.s1Sub")}>
          {data.absorption_x_faktor?.length ? (
            <Table
              cols={[t("analysis.s1c1"), t("analysis.s1c2"), t("analysis.s1c3"), t("analysis.s1c4"), t("analysis.s1c5"), t("analysis.s1c6"), t("analysis.s1c7")]}
              rows={data.absorption_x_faktor.map(r => [
                <span className="text-white font-mono">{r.market}</span>,
                <Score value={r.demand_value_score} />,
                <span className="num">{r.rental_avg_dom}d</span>,
                <span className="num">{r.rental_resolved_pct}%</span>,
                <span className="num text-signal-green">{r.green_n ?? "—"}</span>,
                <span className="num">{r.avg_green_faktor ?? "—"}</span>,
                <span className="num">€{r.avg_price_k ?? "—"}k</span>,
              ])}
            />
          ) : <Empty msg={t("analysis.s1Empty")} />}
        </Section>

        {/* ── 2. DISTRICT RENT REFERENCE ── */}
        <Section
          icon={MapPinned}
          title={t("analysis.districtRentTitle")}
          sub={t("analysis.districtRentSub")}
          testId="district-rent-reference"
        >
          {data.district_rent_reference?.rows?.length ? (
            <>
              <div className="mb-3 text-[10px] font-mono text-text-muted">
                {t("analysis.districtRentSource", {
                  source: data.district_rent_reference.source?.name || "willhaben Mietpreisspiegel",
                  scope: [
                    data.district_rent_reference.source?.basis,
                    data.district_rent_reference.source?.scope,
                    data.district_rent_reference.source?.warning,
                  ].filter(Boolean).join(" · ") || "District-level offer rents per sqm",
                })}
              </div>
              <Table
                cols={[
                  t("analysis.districtRentColsYear"),
                  t("analysis.districtRentColsSegment"),
                  t("analysis.districtRentColsDistrict"),
                  t("analysis.districtRentColsPrice"),
                  t("analysis.districtRentColsDelta"),
                ]}
                rows={data.district_rent_reference.rows.map(r => [
                  <span className="num text-text-secondary">{r.year}</span>,
                  <span className="text-white">{t(`analysis.${segmentKey(r.segment)}`) || r.segment}</span>,
                  <span className="text-white font-mono">{r.district}</span>,
                  <span className="num">{formatEurSqm(r.eur_sqm)}</span>,
                  <span className={`num ${r.delta_pct == null ? "text-text-muted" : r.delta_pct > 0 ? "text-signal-green" : "text-signal-yellow"}`}>
                    {formatPct(r.delta_pct)}
                  </span>,
                ])}
              />
            </>
          ) : <Empty msg={t("analysis.districtRentEmpty")} />}
        </Section>

        {/* ── 3. DISTRICT OFFER BENCHMARK ── */}
        <Section
          icon={MapPinned}
          title={t("analysis.offerBenchmarkTitle")}
          sub={t("analysis.offerBenchmarkSub")}
          testId="district-offer-benchmark"
        >
          {benchmarkRows.length ? (
            <>
              <div className="mb-3 text-[10px] font-mono text-text-muted">
                {t("analysis.offerBenchmarkSource", {
                  source: benchmark.source?.name || "District Offer Benchmark",
                  scope: [
                    benchmark.source?.basis,
                    benchmark.source?.scope,
                    benchmark.source?.warning,
                  ].filter(Boolean).join(" · "),
                })}
              </div>
              <div className="mb-4 flex items-center gap-2 text-[10px] font-mono text-text-muted">
                <span>{t("analysis.offerBenchmarkCountry")}</span>
                <select
                  data-testid="district-offer-country-filter"
                  value={benchmarkCountry}
                  onChange={(e) => setBenchmarkCountry(e.target.value)}
                  className="bg-bg border border-border px-2 py-1 text-[10px] font-mono text-white"
                >
                  <option value="ALL">{t("common.all")}</option>
                  {benchmarkCountries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                <div>
                  <div className="overline mb-2">{t("analysis.offerBenchmarkCityTotals")}</div>
                  <Table
                    cols={[
                      t("analysis.offerBenchmarkColsCity"),
                      t("analysis.offerBenchmarkColsScope"),
                      t("analysis.offerBenchmarkColsPurchase"),
                      t("analysis.offerBenchmarkColsRent"),
                      t("analysis.offerBenchmarkColsYield"),
                    ]}
                    rows={cityBenchmarkRows.map((r) => benchmarkRowCells(r, t))}
                  />
                </div>
                <div>
                  <div className="overline mb-2">{t("analysis.offerBenchmarkDistrictRows")}</div>
                  <Table
                    cols={[
                      t("analysis.offerBenchmarkColsCity"),
                      t("analysis.offerBenchmarkColsScope"),
                      t("analysis.offerBenchmarkColsPurchase"),
                      t("analysis.offerBenchmarkColsRent"),
                      t("analysis.offerBenchmarkColsYield"),
                    ]}
                    rows={districtBenchmarkRows.map((r) => benchmarkRowCells(r, t))}
                  />
                </div>
              </div>
            </>
          ) : <Empty msg={t("analysis.offerBenchmarkEmpty")} />}
        </Section>

        {/* ── 4. FILTER FUNNEL ── */}
        <Section icon={BarChart2} title={t("analysis.s2Title")} sub={t("analysis.s2Sub")}>
          {data.funnel?.length ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
              {data.funnel.map(r => (
                <div key={r.zone} className="bg-bg p-4">
                  <div className={`overline ${
                    (r.zone || "").startsWith("GREEN") ? "text-signal-green"
                      : r.zone === "YELLOW" ? "text-signal-yellow"
                      : "text-text-muted"
                  }`}>{r.zone || "—"}</div>
                  <div className="num text-2xl mt-1 text-white">{r.n?.toLocaleString()}</div>
                  <div className="text-[11px] font-mono text-text-secondary mt-1">
                    {r.pct}% · avg F{r.avg_faktor} · avg {r.avg_size}m²
                  </div>
                </div>
              ))}
            </div>
          ) : <Empty msg={t("analysis.s2Empty")} />}
        </Section>

        {/* ── 5. HKL SUPPLY ── */}
        <Section icon={Home} title={t("analysis.s3Title")} sub={t("analysis.s3Sub")}>
          {data.hkl_supply?.length ? (
            <Table
              cols={[t("analysis.s3c1"), t("analysis.s3c2"), t("analysis.s3c3"), t("analysis.s3c4"), t("analysis.s3c5"), t("analysis.s3c6")]}
              rows={data.hkl_supply.slice(0, 15).map(r => [
                <span className="text-white font-mono">{r.market}</span>,
                <span className={`num font-bold ${r.hkl_count > 0 ? "text-signal-green" : "text-text-muted"}`}>{r.hkl_count}</span>,
                <span className="num">{r.total}</span>,
                <span className="num">{r.avg_faktor}</span>,
                <span className="num">{r.f_lte22}</span>,
                <span className="num">{r.small_n}</span>,
              ])}
            />
          ) : <Empty msg={t("analysis.s3Empty")} />}
        </Section>

        {/* ── 6. SIZE ARBITRAGE ── */}
        <Section icon={BarChart2} title={t("analysis.s4Title")} sub={t("analysis.s4Sub")}>
          {data.size_arbitrage?.length ? (
            <Table
              cols={[t("analysis.s4c1"), t("analysis.s4c2"), t("analysis.s4c3"), t("analysis.s4c4"), t("analysis.s4c5"), t("analysis.s4c6")]}
              rows={data.size_arbitrage.map(r => [
                <span className="text-white font-mono">{r.market}</span>,
                <span className="num">{r.avg_f_small ?? "—"}</span>,
                <span className="num">{r.avg_f_large ?? "—"}</span>,
                <span className="num text-text-muted">{r.n_small}</span>,
                <span className="num text-text-muted">{r.n_large}</span>,
                <PremiumBadge value={r.large_premium} />,
              ])}
            />
          ) : <Empty msg={t("analysis.s4Empty")} />}
        </Section>

        {/* ── 7. PRICE DROP TIMING ── */}
        <Section icon={Clock} title={t("analysis.s5Title")} sub={t("analysis.s5Sub")}>
          {data.price_drop_by_age?.length ? (
            <Table
              cols={[t("analysis.s5c1"), t("analysis.s5c2"), t("analysis.s5c3"), t("analysis.s5c4"), t("analysis.s5c5"), t("analysis.s5c6"), t("analysis.s5c7")]}
              rows={data.price_drop_by_age.map(r => [
                <span className="num text-text-secondary">{r.age_bucket}d</span>,
                <span className="num">{r.n?.toLocaleString()}</span>,
                <span className="num text-signal-green">{r.green_n}</span>,
                <span className="num">{r.avg_faktor}</span>,
                <span className="num">{r.with_drop}</span>,
                <span className={`num ${r.drop_pct > 10 ? "text-signal-green" : "text-text-muted"}`}>{r.drop_pct}%</span>,
                <span className="num">{r.avg_drop_pct != null ? `-${r.avg_drop_pct}%` : "—"}</span>,
              ])}
            />
          ) : <Empty msg={t("analysis.s5Empty")} />}
        </Section>

        {/* ── 8. FIXER-UPPER MARKETS ── */}
        <Section icon={Wrench} title={t("analysis.s6Title")} sub={t("analysis.s6Sub")}>
          {data.fixer_uppers?.length ? (
            <Table
              cols={[t("analysis.s6c1"), t("analysis.s6c2"), t("analysis.s6c3"), t("analysis.s6c4"), t("analysis.s6c5"), t("analysis.s6c6")]}
              rows={data.fixer_uppers.map(r => [
                <span className="text-white font-mono">{r.market}</span>,
                <span className="num">{r.n}</span>,
                <span className={`num font-bold ${r.avg_faktor <= 20 ? "text-signal-green" : r.avg_faktor <= 24 ? "text-signal-yellow" : "text-text-muted"}`}>{r.avg_faktor}</span>,
                <span className="num">€{r.avg_price_k}k</span>,
                <span className="num">{r.avg_size}m²</span>,
                <span className="num">{r.avg_cond ?? "—"}</span>,
              ])}
            />
          ) : <Empty msg={t("analysis.s6Empty")} />}
        </Section>

        {/* ── 9. MRG RISK ── */}
        <Section icon={AlertTriangle} title={t("analysis.s7Title")} sub={t("analysis.s7Sub")}>
          {data.mrg_risk?.length ? (
            <Table
              cols={[t("analysis.s7c1"), t("analysis.s7c2"), t("analysis.s7c3"), t("analysis.s7c4"), t("analysis.s7c5"), t("analysis.s7c6")]}
              rows={data.mrg_risk.map(r => [
                <span className="text-white font-mono">{r.market}</span>,
                <span className="num">{r.green_n}</span>,
                <span className="num text-signal-yellow">{r.unenriched}</span>,
                <span className={`num ${r.unenriched_pct > 50 ? "text-signal-yellow" : "text-text-muted"}`}>{r.unenriched_pct}%</span>,
                <span className={`num ${r.mrg_falle > 0 ? "text-signal-red" : "text-text-muted"}`}>{r.mrg_falle}</span>,
                <span className="num">{r.altbau}</span>,
              ])}
            />
          ) : <Empty msg={t("analysis.s7Empty")} />}
        </Section>

        {/* ── 10. MARKET TIGHTNESS ── */}
        <Section icon={Package} title={t("analysis.s8Title")} sub={t("analysis.s8Sub")}>
          {data.market_tightness?.length ? (
            <Table
              cols={[t("analysis.s8c1"), t("analysis.s8c2"), t("analysis.s8c3"), t("analysis.s8c4"), t("analysis.s8c5")]}
              rows={data.market_tightness.map(r => [
                <span className="text-white font-mono">{r.market}</span>,
                <span className="num">{r.total_seen?.toLocaleString()}</span>,
                <span className="num text-signal-green">{r.green_total}</span>,
                <span className={`num font-bold ${r.green_new_7d > 0 ? "text-signal-green" : "text-text-muted"}`}>{r.green_new_7d}</span>,
                <span className="num">{r.green_hit_rate}%</span>,
              ])}
            />
          ) : <Empty msg={t("analysis.s8Empty")} />}
        </Section>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, sub, children, testId }) {
  return (
    <div className="px-6 md:px-10 py-8" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className="text-signal-green flex-shrink-0" />
        <div className="font-heading font-black text-base tracking-tight">{title}</div>
      </div>
      <div className="text-[11px] font-mono text-text-muted mb-4">{sub}</div>
      {children}
    </div>
  );
}

function Table({ cols, rows }) {
  return (
    <div className="overflow-x-auto border border-border">
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="border-b border-border bg-surface">
            {cols.map(c => (
              <th key={c} className="px-3 py-2 text-left overline text-text-muted whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/50 hover:bg-surface/50">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Score({ value }) {
  if (value == null) return <span className="text-text-muted">—</span>;
  const color = value >= 300 ? "text-signal-green" : value >= 150 ? "text-signal-yellow" : "text-text-muted";
  return <span className={`num font-bold ${color}`}>{value}</span>;
}

function PremiumBadge({ value }) {
  if (value == null) return <span className="text-text-muted">—</span>;
  // negative large_premium = small units cheaper = HKL advantage
  const isGood = value < 0;
  const color = isGood ? "text-signal-green" : value > 2 ? "text-signal-yellow" : "text-text-muted";
  return (
    <span className={`num ${color}`}>
      {value > 0 ? "+" : ""}{value} {isGood ? "✓small" : ""}
    </span>
  );
}

function Empty({ msg }) {
  return <p className="text-[11px] font-mono text-text-muted py-4">{msg}</p>;
}

function formatEurSqm(value) {
  if (value == null) return "—";
  return `€${Number(value).toLocaleString("de-AT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatPct(value) {
  if (value == null) return "—";
  return `${value > 0 ? "+" : ""}${Number(value).toFixed(1)}%`;
}

function segmentKey(segment) {
  const map = {
    "fastest growth": "districtRentSegmentFastestGrowth",
    "cheapest district": "districtRentSegmentCheapest",
    "highest price": "districtRentSegmentHighest",
    "declining market": "districtRentSegmentDeclining",
  };
  return map[segment] || "districtRentSegmentOther";
}

function topBenchmarkRows(rows, kind) {
  return rows
    .filter((r) => (kind === "city_total" ? r.kind === "city_total" : r.kind !== "city_total"))
    .sort((a, b) => (Number(b.gross_yield) || 0) - (Number(a.gross_yield) || 0))
    .slice(0, 10);
}

function benchmarkRowCells(r, t) {
  return [
    <span className="text-white font-mono">{r.city || "—"}</span>,
    <span className="text-white">{r.kind === "city_total" ? t("analysis.offerBenchmarkCityTotal") : (r.postal_code || r.district || "—")}</span>,
    <span className="num">{formatEurSqm(r.purchase_price_eur_sqm)}</span>,
    <span className="num">{formatEurSqm(r.rent_eur_sqm)}</span>,
    <span className="num text-signal-green">{formatPct((Number(r.gross_yield) || 0) * 100)}</span>,
  ];
}
